// Sprint T-22 · NotificationList · infinite scroll · UI-only (data provided)
import React, { useCallback, useEffect, useRef } from "react";
import { NotificationItem } from "./NotificationItem";
import { NotificationSkeleton } from "./NotificationSkeleton";
import { NotificationEmptyState } from "./NotificationEmptyState";

/**
 * Lista con infinite scroll (intersection observer sobre el sentinel).
 *
 * Props:
 *   · items          → array de notificaciones
 *   · loading        → primer fetch en curso
 *   · loadingMore    → paginación en curso
 *   · hasMore        → indica si quedan más páginas por cargar
 *   · error          → string | null
 *   · onLoadMore     → callback llamado al llegar al bottom
 *   · onOpen         → click en item → deep_link
 *   · onMarkRead     · onMarkUnread · onDismiss
 *   · unreadOnly     → contexto para empty state
 *   · hasSearch      → contexto para empty state
 *   · hasKindFilter  → contexto para empty state
 */
export const NotificationList = ({
  items = [],
  loading = false,
  loadingMore = false,
  hasMore = false,
  error = null,
  onLoadMore,
  onOpen,
  onMarkRead,
  onMarkUnread,
  onDismiss,
  unreadOnly = false,
  hasSearch = false,
  hasKindFilter = false,
}) => {
  const sentinelRef = useRef(null);

  const stableLoadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    onLoadMore?.();
  }, [loadingMore, hasMore, onLoadMore]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) stableLoadMore();
      },
      { root: null, rootMargin: "80px", threshold: 0 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, stableLoadMore]);

  if (loading && items.length === 0) {
    return <NotificationSkeleton count={6} />;
  }
  if (error) {
    return (
      <div
        className="px-6 py-10 text-center text-sm text-red-300"
        data-testid="notif-list-error"
      >
        {error}
      </div>
    );
  }
  if (!loading && items.length === 0) {
    return (
      <NotificationEmptyState
        unreadOnly={unreadOnly}
        hasSearch={hasSearch}
        hasKindFilter={hasKindFilter}
      />
    );
  }

  return (
    <div data-testid="notif-list">
      <ul aria-live="polite" aria-relevant="additions">
        {items.map((n) => (
          <NotificationItem
            key={n.id}
            n={n}
            onOpen={onOpen}
            onMarkRead={onMarkRead}
            onMarkUnread={onMarkUnread}
            onDismiss={onDismiss}
          />
        ))}
      </ul>
      {/* Sentinel para infinite scroll */}
      {hasMore && (
        <div
          ref={sentinelRef}
          className="py-4 text-center text-xs text-[var(--ag-text-dim)]"
          data-testid="notif-list-sentinel"
        >
          {loadingMore ? "Cargando más…" : ""}
        </div>
      )}
    </div>
  );
};

export default NotificationList;
